/**
 * Theme Context
 *
 * Manages dynamic theme state for tenant-specific theming.
 * Provides access to the current theme and a function to update the primary color.
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Theme } from '@mui/material/styles';
import { createAppTheme } from '../theme/themeFactory';

interface ThemeContextType {
  theme: Theme;
  updateThemeColor: (primaryColor: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  // Initialize with default theme
  const [theme, setTheme] = useState<Theme>(createAppTheme());

  /**
   * Updates the theme with a new primary color
   * @param primaryColor Hex or RGB color string from tenant API
   */
  const updateThemeColor = (primaryColor: string) => {
    const newTheme = createAppTheme(primaryColor);
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, updateThemeColor }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 * @returns Theme context with current theme and update function
 */
export const useAppTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within ThemeContextProvider');
  }
  return context;
};
